import { Field, FilterQuery } from '@yes.technology/react-toolkit'
import { useCallback, useMemo, useState } from 'react'
import { targetComponents } from 'site/Renderer/components/SelectAndLink/SelectAndLink.constants'
import { useSelectAndLink } from 'site/Renderer/components/SelectAndLink'
import {
  FilterFieldsFormContextProvider,
  useFilterFieldsContext
} from '../../context'
import { toYesFilter } from '../../shared/toYesFilter/toYesFilter'
import { GroupedFieldValues } from '../../types'
import Section from '../Section/Section'
import FilterFormView from './FilterFormView'
import useFilterFormActions from './hooks/useFilterFormActions'
import useFilterFormSubmit from './hooks/useFilterFormSubmit'
import useSyncFieldsValuesAfterSelectAndLink from './hooks/useSelectAndLinkFieldValuesEffect'
import useGroupedFieldValues from './useGroupedFieldValues'

type FilterFormProps = {
  initialGroupedFieldValues: GroupedFieldValues
  sections: Field[][]
  title?: string
  onSubmit?: (query: ReturnType<typeof toYesFilter>) => void
  defaultExecutedFilteraction?: string
  clearFilterQuery?: (value: Partial<FilterQuery | undefined>) => void
}

const FilterForm = ({
  initialGroupedFieldValues,
  sections,
  title,
  onSubmit,
  defaultExecutedFilteraction,
  clearFilterQuery
}: FilterFormProps) => {
  const { getSession, closeSelectAndLink, onClearSelection } =
    useSelectAndLink()
  const selectAndLinkSession =
    getSession(targetComponents.FILTER_FIELDS) ?? null

  const clearFilterFieldsSelection = useCallback(
    () => onClearSelection(targetComponents.FILTER_FIELDS),
    [onClearSelection]
  )

  const { groupedFieldValues, handleChange } = useGroupedFieldValues(
    initialGroupedFieldValues
  )

  useSyncFieldsValuesAfterSelectAndLink({
    selectAndLinkSession,
    groupedFieldValues,
    handleChange,
    closeSelectAndLink,
    onClearSelection: clearFilterFieldsSelection
  })

  const [composition, handleComposition] = useState('1')

  const { submitFieldValues } = useFilterFormSubmit({ onSubmit })

  const { handleClear, onFieldChange } = useFilterFormActions({
    handleChange,
    handleComposition,
    clearFilterQuery,
    onClearSelection: clearFilterFieldsSelection
  })

  const onSubmitForm = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      submitFieldValues(groupedFieldValues, composition)
    },
    [groupedFieldValues, composition, submitFieldValues]
  )

  const formContextValue = useMemo(
    () => ({
      groupedFieldValues,
      composition,
      initialGroupedFieldValues,
      sections,
      title,
      defaultExecutedFilteraction,
      handleChange: onFieldChange,
      handleComposition,
      handleClear,
      executeFilter: submitFieldValues,
      clearFilterQuery
    }),
    [
      groupedFieldValues,
      composition,
      initialGroupedFieldValues,
      sections,
      title,
      defaultExecutedFilteraction,
      onFieldChange,
      handleComposition,
      handleClear,
      submitFieldValues,
      clearFilterQuery
    ]
  )

  const { styling } = useFilterFieldsContext()
  const { style } = styling

  return (
    <FilterFieldsFormContextProvider value={formContextValue}>
      <FilterFormView style={style} onSubmitForm={onSubmitForm}>
        <Section />
      </FilterFormView>
    </FilterFieldsFormContextProvider>
  )
}

export default FilterForm
